import React from 'react';
import { CannyChangelog } from 'src/changelog/views/CannyChangelog';
import { CannyProvider } from 'src/changelog/views/CannyProvider';
import HeaderCreateJourneyButton from 'src/dashboard/components/header/buttons/create-journey';
import { useDashboardInboxStore } from 'src/dashboard/inbox/store';
import useDashboardStore from 'src/dashboard/store/dashboard';
import {
  DoubleChatStrongIcon,
  FolderRegularIcon,
  FolderStrongIcon,
  GridStrongIcon,
  TemplateStrongIcon,
  CalendarStrongIcon,
} from 'src/monet/icons';
import ActivityStrongIcon from 'src/monet/icons/ActivityStrongIcon';
import VideoStrongIcon from 'src/monet/icons/VideoStrongIcon';
import SettingsSlidersStrongIcon from 'src/monet/icons/SettingsSlidersStrongIcon';
import { PageSidebarHelpMenu } from './help-menu';
import { PageSidebarItem } from './item';
import { useJourneys } from 'src/store/journeys';
import classNames from 'classnames';
import { FreeGatingEnum, useFreeGatingStore } from 'src/common/free-gating/store';
import { FreeGatingTooltip } from 'src/common/free-gating/tooltip';
import { useCurrentOrganization } from 'src/store/organization';
import { ChartBarIcon, HomeIcon, LockClosedIcon } from '@heroicons/react/outline';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';
import { AffiliateButton } from 'src/affiliate/AffiliateButton';

export const PageSidebar = () => {
  const {
    daysLeftInTrial,
  } = useCurrentSubscription();
  
  const { features, showUpgradeModal, isOnTrial, isSubscribed } = useFeatureRestrictions();

  // Show lock icon for Analytics if user doesn't have access to advanced insights (Grow plan users)
  const analyticsLocked = !features.canAccessAdvancedInsights;
  // Show lock icon for Session Replay if user doesn't have access to session replay
  const sessionLocked = !features.canAccessSessionReplay;
  // Show lock icon for other features if user is not subscribed or not on trial
  const otherLocked = (!isOnTrial && !isSubscribed);

  const totalUnreadMessageCount = useDashboardInboxStore((state) => state.totalUnreadMessageCount);
  const activeTab = useDashboardStore((state) => state.activeTab);

  const isFreeOrg = useCurrentOrganization((state) => state.isFreeOrg);
  const hasReachedFreeLimit = useJourneys((state) => state.hasReachedFreeLimit);
  const setFreeGatingModalByType = useFreeGatingStore((state) => state.setFreeGatingModalByType);

  const getFullPathWithQueryParams = (path: string) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('create', 'template');
    return `${path}?${queryParams.toString()}`;
  };

  const toggleAnalyticsPaywall = () => {
    if (!features.canAccessAdvancedInsights) {
      showUpgradeModal('advanced_insights', 
        'Advanced analytics provide detailed insights into how users interact with your journeys. Track engagement, completion rates, and user behavior.<br/><br/>To unlock advanced analytics, upgrade to the Scale Plan',
        'Unlock Advanced Analytics'
      );
    }
  };

  const toggleSessionPaywall = () => {
    if (!features.canAccessSessionReplay) {
      showUpgradeModal('session_replay', 
        'Session replay is a reconstructed presentation of how a user interacted with your journeys. It captures mouse clicks, movements etc giving you deeper insights<br/><br/>To unlock session replays, upgrade to the Scale Plan',
        'Unlock Session Replay'
      );
    }
  };

  return (
    <div className='flex flex-col gap-6'>
      <div
        className={classNames('flex flex-col', {
          'space-y-2': (isFreeOrg && !hasReachedFreeLimit) || (!isFreeOrg && !features.canAccessAdvancedInsights && !hasReachedFreeLimit),
        })}
      >
        <FreeGatingTooltip>
          <HeaderCreateJourneyButton />
        </FreeGatingTooltip>
      </div>
      <div className='flex flex-col'>
        <PageSidebarItem
          target='/home'
          isActive={activeTab === 'home'}
          trackingName='home'
          className='home-panel-option'
        >
          <>
            <HomeIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong' style={{ marginTop: 2 }}>
              Home
            </span>
          </>
        </PageSidebarItem>
        <PageSidebarItem
          target='/journeys'
          isActive={activeTab === 'journeys'}
          trackingName='home'
          className='home-panel-option'
        >
          <>
            <GridStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Journeys</span>
          </>
        </PageSidebarItem>
        <PageSidebarItem
          target={getFullPathWithQueryParams('/journeys/all')}
          isActive={activeTab === 'template'}
          trackingName='templates'
        >
          <>
            <FolderStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Templates</span>
          </>
        </PageSidebarItem>
        
        <PageSidebarItem target='/insights/journeys' isActive={activeTab === 'insights'} trackingName='insights'>
          <>
            <ActivityStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Insights</span>
          </>
        </PageSidebarItem>
        <PageSidebarItem
          className='relative'
          onClick={() => toggleSessionPaywall()}
          target='/sessions'
          isActive={activeTab === 'sessions'}
          trackingName='sessions'
        >
          <>
            <VideoStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Session Replays</span>{sessionLocked && <LockClosedIcon className='w-4 h-4 ml-1 text-gray-500' />}
            {/* <div
              style={{ top: 0, right: '25px', zIndex: 20 }}
              className='ml-2 absolute rounded-lg block bg-rose-200 text-rose-700 px-2 py-1 text-xs font-medium font-poppins '
            >
              new
            </div> */}
          </>
        </PageSidebarItem>
        <PageSidebarItem
          onClick={() => toggleAnalyticsPaywall()}
          target='/analytics/journeys'
          isActive={activeTab === 'analytics'}
          trackingName='analytics'
        >
          <>
            <ChartBarIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Analytics</span>{analyticsLocked && <LockClosedIcon className='w-4 h-4 ml-1 text-gray-500' />}
          </>
        </PageSidebarItem>
        <PageSidebarItem
          target='/inbox'
          isActive={activeTab === 'inbox'}
          renderBadge={
            <>
              {totalUnreadMessageCount > 0 && (
                <div className='ml-auto px-1.5 flex justify-center items-center min-w-[1.125rem] rounded-lg text-[10px] leading-[18px] font-[450] text-bedrock-white bg-bedrock-brand-secondary'>
                  {totalUnreadMessageCount}
                </div>
              )}
            </>
          }
          trackingName='comments'
        >
          <>
            <DoubleChatStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Comments</span>
          </>
        </PageSidebarItem>
        <PageSidebarItem target='/library/content' isActive={activeTab === 'library'} trackingName='library'>
          <>
            <TemplateStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Library</span>
          </>
        </PageSidebarItem>
        <PageSidebarItem target='/organization/settings' isActive={activeTab === 'settings'} trackingName='settings'>
          <>
            <SettingsSlidersStrongIcon className='w-4 h-4' />
            <span className='text-bedrock-p-strong'>Settings</span>
          </>
        </PageSidebarItem>
        <PageSidebarHelpMenu />

        <AffiliateButton className='change-log-button' align='left' position='bottom' />

        <CannyProvider appId={'65de318acf45498966b2fc19'}>
          <CannyChangelog type='button' className='change-log-button' align='left' position='bottom' />
        </CannyProvider>
        
        {isOnTrial && daysLeftInTrial > 0 && (
          <div className='mt-4 rounded-lg bg-yellow-50 border border-yellow-600 p-3'>
            <div className='flex items-center space-x-2'>
              <CalendarStrongIcon className='w-5 h-5 text-yellow-600' />
              <div className='flex flex-col'>
                <span className='text-bedrock-p-strong text-yellow-800'>Trial Period</span>
                <span className='text-bedrock-p-small text-yellow-800'>
                  {daysLeftInTrial} {daysLeftInTrial === 1 ? 'day' : 'days'} remaining
                </span>
              </div>
            </div>
            <div className='w-full bg-gray-200 h-2 rounded-full mt-2'>
              <div className='h-2 bg-yellow-600 rounded-full' style={{ width: `${((14 - daysLeftInTrial) / 14) * 100}%` }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
